/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, April 16th 2022, 12:32:16 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { TaskTemplate, NODE_TYPES } from "lib/schemas";
import { UserTaskTypeSelector } from "./UserTaskTypeSelector";
import { AutomatedTaskTypeSelector } from "./AutomatedTaskTypeSelector";
import { EventTaskTypeSelector } from "./EventTaskTypeSelector";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Icon as BPMNIcon } from "@iconify/react";
import BPMNUserTaskIcon from "@iconify/icons-bpmn/user";
import BPMNServiceTaskIcon from "@iconify/icons-bpmn/service";
import { Typography, IconText, ActionDialog, useToggleState } from "ui-widgets";
import { COLORSCHEME } from "theme/styles";
import {
  ServiceBuilderSelectorDialog,
  ServiceBuilderSelectorDialogProps,
} from "service-builder/components/core/ServiceBuilderSelectorDialog";

interface NewTaskInfoProps extends Pick<ServiceBuilderSelectorDialogProps, "open" | "onClose"> {
  createNewTaskTemplate: (
    type: keyof typeof NODE_TYPES,
    taskTitle: string,
    optionalProperties: Partial<TaskTemplate>
  ) => void;
  nodeType: keyof typeof NODE_TYPES;
}

export const NewTaskInfo = (props: NewTaskInfoProps) => {
  const [taskTitle, setTaskTitle] = React.useState<string>();

  const {
    handleClose: closeTitleRequiredFn,
    handleOpen: openTitleRequiredFn,
    open: titleRequiredOpenState,
  } = useToggleState();

  const saveNewTaskInfo = (optionalProperties: Partial<TaskTemplate>) => {
    if (!taskTitle) {
      openTitleRequiredFn();
      return;
    }

    props.createNewTaskTemplate(props.nodeType, taskTitle, optionalProperties);

    setTaskTitle(null); // Reset title

    props.onClose(null, "backdropClick");
  };

  const popUpTitle =
    props.nodeType === "View" ? (
      <UserTaskPopUpTitle />
    ) : props.nodeType === "Handler" ? (
      <ServiceTaskPopUpTitle />
    ) : null;

  return (
    <>
      <ActionDialog
        severity="warning"
        title="Enter Title"
        open={titleRequiredOpenState}
        onClose={closeTitleRequiredFn}
        defaultActionsType="Close"
        confirmationAction={() => {}}
        dialogMessage={<p>Kindly provide a title for the task template.</p>}
      />

      <ServiceBuilderSelectorDialog popUpTitle={popUpTitle} open={props.open} onClose={props.onClose}>
        <Grid container sx={{ marginTop: 1 }} spacing={2}>
          <Grid item md={12}>
            <TextField
              label="Task Title"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTaskTitle(event.target.value)}
              required
              fullWidth
            />
          </Grid>
          {props.nodeType === "View" ? (
            <>
              <Grid item md={12}>
                <Typography variant="h6" color={COLORSCHEME.primary}>
                  Choose a user task type from the options below
                </Typography>
              </Grid>
              <UserTaskTypeSelector saveNewTaskInfo={saveNewTaskInfo} />
            </>
          ) : null}

          {props.nodeType === "Handler" ? (
            <>
              <Grid item md={12}>
                <Typography variant="h6" color={COLORSCHEME.primary}>
                  Choose a service task type from the options below
                </Typography>
              </Grid>
              <AutomatedTaskTypeSelector saveNewTaskInfo={saveNewTaskInfo} />
            </>
          ) : null}
          {props.nodeType === "Event" ? (
            <>
              <Grid item md={12}>
                <Typography variant="h6" color={COLORSCHEME.primary}>
                  Create or select an event code
                </Typography>
                <div style={{ padding: 20 }}>
                  <EventTaskTypeSelector saveNewTaskInfo={saveNewTaskInfo} />
                </div>
              </Grid>
            </>
          ) : null}
        </Grid>
      </ServiceBuilderSelectorDialog>
    </>
  );
};

const UserTaskPopUpTitle = () => (
  <Typography>
    <IconText
      icon={<BPMNIcon icon={BPMNUserTaskIcon} color={COLORSCHEME.secondary} />}
      textComponent={<Typography variant="h5">Choose a User Task Type</Typography>}
    />
  </Typography>
);

const ServiceTaskPopUpTitle = () => (
  <Typography>
    <IconText
      icon={<BPMNIcon icon={BPMNServiceTaskIcon} color={COLORSCHEME.secondary} />}
      textComponent={<Typography variant="h5">Choose a Service Task Type</Typography>}
    />
  </Typography>
);
